import { createSlice } from "@reduxjs/toolkit";
import {
  InitiateDepositRequest,
  cancelPayment,
  initiateDeposit,
  validatePayment
} from "src/network/graphql/stripeService";
import { StoreDispatch, RootState } from "../store";
import { markStripeCheckoutStart } from "src/utils";
import { searchOrder, SearchOrderRequest, getOrder, getPayments } from "src/network/graphql/paymentService";
import { initiateSubscription } from "src/network/graphql/subscriptionServices";
import type { initiateSubscriptionRequest } from "src/network/graphql/subscriptionServices";
import { EMPTY_STRING } from "src/components/constant/constant";

export type StripeValidationStatus = "processing" | "success" | "failure" | "pending" | "refunded";

export type StripePaymentDetails = {
  order_id: string | null;
  payment_id: string | null;
  payment_display_id: string | null;
  order_display_id: string | null;
  payment_status: string | null;
  amount: number | null;
  provider_payment_id: string | null;
  validationStatus: StripeValidationStatus;
};

export type Order = {
  order_id: string;
  amount: number;
  display_id: string;
  status: string;
  organisation_id: string;
  user: {
    attributes: {
      first_name: string;
      last_name: string;
    };
    display_id: string;
    email: string;
    user_id: string;
    username: string;
  };
};

export type OrderItem = {
  order_item_id: string;
  object_id: string;
  amount: number;
  order_item_type: string;
};

export type OrderDetails = {
  items: OrderItem[];
};

const initialOrder: Order[] = [];

interface StripeTransactionHistoryState {
  checkoutLoading: boolean;
  validateLoading: boolean;
  activeSessionId: string | null;
  validationError: unknown;
  lastCheckoutUrl: string;
  paymentDetails: StripePaymentDetails;
  orderListLoading: boolean;
  orders: {
    list: Order[];
    total: number;
  };
  orderDetails: Record<string, OrderDetails>;
  loadingOrderDetails: Record<string, boolean>;
  paymentsList: {
    list: StripePaymentDetails[];
    loading: boolean;
    orderId: string | null;
    orderDisplayId: string | null;
  };
  validatingPaymentId: string | null;
  cancelPaymentLoading: boolean;
}

const initialState: StripeTransactionHistoryState = {
  checkoutLoading: false,
  validateLoading: false,
  activeSessionId: null,
  validationError: null,
  lastCheckoutUrl: EMPTY_STRING,
  paymentDetails: {
    order_id: null,
    payment_id: null,
    payment_display_id: null,
    order_display_id: null,
    payment_status: null,
    amount: null,
    provider_payment_id: null,
    validationStatus: "processing"
  },
  orderListLoading: false,
  orders: {
    list: initialOrder,
    total: 0
  },
  orderDetails: {},
  loadingOrderDetails: {},
  paymentsList: {
    list: [],
    loading: false,
    orderId: null,
    orderDisplayId: null
  },
  validatingPaymentId: null,
  cancelPaymentLoading: false
};

const stripeTransactionHistorySlice = createSlice({
  name: "stripeTransactionHistory",
  initialState,
  reducers: {
    initiateCheckoutStart: (state) => {
      state.checkoutLoading = true;
    },
    initiateCheckoutSuccess: (state, action) => {
      state.checkoutLoading = false;
      state.lastCheckoutUrl = action.payload ?? EMPTY_STRING;
    },
    initiateCheckoutFail: (state, action) => {
      state.checkoutLoading = false;
      state.validationError = action.payload;
    },
    validatePaymentStart: (state, action) => {
      state.validateLoading = true;
      state.validationError = null;
      state.paymentDetails.validationStatus = "processing";
      state.activeSessionId = action.payload;
    },
    validatePaymentSuccess: (state, action) => {
      state.validateLoading = false;
      state.paymentDetails = {
        order_id: action.payload.order_id ?? state.paymentDetails.order_id,
        payment_id: action.payload.payment_id ?? state.paymentDetails.payment_id,
        payment_display_id: action.payload.payment_display_id ?? state.paymentDetails.payment_display_id,
        order_display_id: action.payload.order_display_id ?? state.paymentDetails.order_display_id,
        payment_status: action.payload.payment_status ?? state.paymentDetails.payment_status,
        amount: action.payload.amount ?? state.paymentDetails.amount,
        provider_payment_id: action.payload.provider_payment_id ?? state.paymentDetails.provider_payment_id,
        validationStatus: action.payload.validationStatus ?? state.paymentDetails.validationStatus
      };
    },
    validatePaymentFail: (state, action) => {
      state.validateLoading = false;
      state.validationError = action.payload;
      state.paymentDetails = {
        order_id: null,
        payment_id: null,
        payment_display_id: null,
        order_display_id: null,
        payment_status: null,
        amount: null,
        provider_payment_id: null,
        validationStatus: "failure"
      };
    },
    resetStripeValidationState: (state) => {
      state.validateLoading = false;
      state.validationError = null;
      state.activeSessionId = null;
      state.paymentDetails = {
        validationStatus: "processing",
        order_id: null,
        payment_id: null,
        payment_display_id: null,
        order_display_id: null,
        payment_status: null,
        amount: null,
        provider_payment_id: null
      };
    },
    fetchOrderListStart: (state) => {
      state.orderListLoading = true;
    },
    fetchOrderListSuccess: (state, action) => {
      state.orderListLoading = false;
      state.orders.list = action.payload?.list ?? [];
      state.orders.total = action.payload?.total ?? 0;
    },
    fetchOrderListFail: (state, action) => {
      state.orderListLoading = false;
      state.orders.list = [];
      state.orders.total = 0;
      state.validationError = action.payload;
    },
    fetchOrderDetailsStart: (state, action) => {
      state.loadingOrderDetails[action.payload] = true;
    },
    fetchOrderDetailsSuccess: (state, action) => {
      const { orderId, items } = action.payload;

      state.orderDetails[orderId] = { items };
      delete state.loadingOrderDetails[orderId];
    },
    fetchOrderDetailsFail: (state, action) => {
      const orderId = action.payload.orderId;

      delete state.loadingOrderDetails[orderId];
      state.validationError = action.payload.error;
    },
    fetchPaymentsListStart: (state, action) => {
      state.paymentsList.loading = true;
      state.paymentsList.orderId = action.payload.orderId;
      state.paymentsList.orderDisplayId = action.payload.orderDisplayId;
    },
    fetchPaymentsListSuccess: (state, action) => {
      state.paymentsList.loading = false;
      state.paymentsList.list = action.payload;
    },
    fetchPaymentsListFail: (state, action) => {
      state.paymentsList.loading = false;
      state.paymentsList.list = [];
      state.validationError = action.payload;
    },
    validateSinglePaymentStart: (state, action) => {
      state.validatingPaymentId = action.payload;
    },
    validateSinglePaymentSuccess: (state, action) => {
      const { paymentId, paymentData } = action.payload;
      const paymentIndex = state.paymentsList.list.findIndex((p) => p.payment_id === paymentId);

      if (paymentIndex !== -1) {
        state.paymentsList.list[paymentIndex] = {
          ...state.paymentsList.list[paymentIndex],
          validationStatus: paymentData.validationStatus,
          payment_status: paymentData.payment_status,
          amount: paymentData.amount ?? state.paymentsList.list[paymentIndex].amount
        };
      }
      state.validatingPaymentId = null;
    },
    validateSinglePaymentFail: (state, action) => {
      state.validatingPaymentId = null;
      state.validationError = action.payload;
    },
    resetPaymentsList: (state) => {
      state.paymentsList.list = [];
      state.paymentsList.orderId = null;
      state.paymentsList.orderDisplayId = null;
      state.paymentsList.loading = false;
    },
    cancelPaymentStart: (state) => {
      state.cancelPaymentLoading = true;
    },
    cancelPaymentSuccess: (state) => {
      state.cancelPaymentLoading = false;
    },
    cancelPaymentFail: (state, action) => {
      state.cancelPaymentLoading = false;
      state.validationError = action.payload;
    }
  }
});

const {
  fetchOrderListStart,
  fetchOrderListSuccess,
  fetchOrderListFail,
  fetchOrderDetailsStart,
  fetchOrderDetailsSuccess,
  fetchOrderDetailsFail,
  fetchPaymentsListStart,
  fetchPaymentsListSuccess,
  fetchPaymentsListFail,
  validateSinglePaymentStart,
  validateSinglePaymentSuccess,
  validateSinglePaymentFail,
  cancelPaymentStart,
  cancelPaymentSuccess,
  cancelPaymentFail
} = stripeTransactionHistorySlice.actions;

export const {
  initiateCheckoutStart,
  initiateCheckoutSuccess,
  initiateCheckoutFail,
  validatePaymentStart,
  validatePaymentSuccess,
  validatePaymentFail,
  resetStripeValidationState,
  resetPaymentsList
} = stripeTransactionHistorySlice.actions;

export const normalizePaymentStatus = (status: string): StripeValidationStatus => {
  switch (status) {
    case "SUCCESS":
      return "success";
    case "FAILED":
    case "CANCELLED":
      return "failure";
    case "PENDING":
      return "pending";
    case "REFUNDED":
    case "PARTIALLY_REFUNDED":
      return "refunded";
    case "PROCESSING":
      return "processing";
    default:
      return "processing";
  }
};

export const fetchOrderListAction = (data: SearchOrderRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchOrderListStart());
    try {
      const response = await searchOrder(data);

      const structuredResponse = response.data.search_order?.items ?? [];

      dispatch(
        fetchOrderListSuccess({
          list: structuredResponse,
          total: response.data.search_order?.total_size ?? 0
        })
      );
    } catch (error) {
      dispatch(fetchOrderListFail(error));
    }
  };
};

export const initiateStripeCheckoutAction = (data: InitiateDepositRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(initiateCheckoutStart());

    try {
      const response = await initiateDeposit(data);
      const url = response?.data?.initiate_deposit?.url;

      dispatch(initiateCheckoutSuccess(url));

      if (cb) cb();

      if (url && typeof window !== "undefined") {
        markStripeCheckoutStart();
        window.location.replace(url);
      }
    } catch (error) {
      dispatch(initiateCheckoutFail(error));
    }
  };
};

export const validateStripePaymentAction = (sessionId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(validatePaymentStart(sessionId));

    try {
      const response = await validatePayment(sessionId);
      const data = response?.data?.verify_payment;

      const normalizedStatus = normalizePaymentStatus(data.payment_status) as StripeValidationStatus;

      if (normalizedStatus === "success") {
        await new Promise((resolve) => setTimeout(resolve, 3000));
      }

      dispatch(
        validatePaymentSuccess({
          validationStatus: normalizedStatus,
          order_id: data?.order_id,
          payment_id: data?.payment_id,
          payment_display_id: data?.payment_display_id,
          order_display_id: data?.order_display_id,
          payment_status: data?.payment_status
        })
      );

      if (data?.order_id) {
        try {
          const paymentsResponse = await getPayments(data.order_id);
          const paymentsData = paymentsResponse.data.get_payments;

          if (data?.payment_id && paymentsData.length > 0) {
            const matchedPayment = paymentsData.find((p: { payment_id: string }) => p.payment_id === data.payment_id);

            if (matchedPayment) {
              const matchedStatus = normalizePaymentStatus(matchedPayment.payment_status) as StripeValidationStatus;

              dispatch(
                validatePaymentSuccess({
                  validationStatus: matchedStatus,
                  order_id: matchedPayment.order_id,
                  payment_id: matchedPayment.payment_id,
                  payment_display_id: matchedPayment.display_id,
                  order_display_id: data?.order_display_id,
                  payment_status: matchedPayment.payment_status,
                  amount: matchedPayment.amount,
                  provider_payment_id: matchedPayment.provider_payment_id
                })
              );
            }
          }
        } catch (paymentsError) {
          console.error("Failed to fetch payments after validation:", paymentsError);
        }
      }
    } catch (error) {
      dispatch(validatePaymentFail(error));
    }
  };
};

export const fetchOrderDetailsAction = (orderId: string) => {
  return async (dispatch: StoreDispatch, getState: () => RootState) => {
    const state = getState();
    const existingDetails = state.stripeTransactionHistory.orderDetails[orderId];
    const isLoading = state.stripeTransactionHistory.loadingOrderDetails[orderId];

    // Don't fetch if already loaded or currently loading
    if (existingDetails || isLoading) {
      return;
    }

    dispatch(fetchOrderDetailsStart(orderId));

    try {
      const response = await getOrder(orderId);
      const orderData = response.data.get_order;

      dispatch(
        fetchOrderDetailsSuccess({
          orderId,
          items: orderData?.items || []
        })
      );
    } catch (error) {
      dispatch(
        fetchOrderDetailsFail({
          orderId,
          error
        })
      );
    }
  };
};

export const fetchPaymentsListAction = (orderId: string, orderDisplayId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchPaymentsListStart({ orderId, orderDisplayId }));

    try {
      const response = await getPayments(orderId);
      const paymentData = response.data.get_payments;

      // Transform payments to StripePaymentDetails format
      const paymentsList: StripePaymentDetails[] = paymentData.map((payment: any) => {
        const normalizedStatus = normalizePaymentStatus(payment.payment_status || "PROCESSING");

        return {
          order_id: payment.order_id,
          payment_id: payment.payment_id,
          payment_display_id: payment.display_id,
          order_display_id: orderDisplayId,
          payment_status: payment.payment_status,
          amount: payment.amount,
          provider_payment_id: payment.provider_payment_id,
          validationStatus: normalizedStatus
        };
      });

      dispatch(fetchPaymentsListSuccess(paymentsList));
    } catch (error) {
      dispatch(fetchPaymentsListFail(error));
    }
  };
};

export const validateSinglePaymentAction = (providerPaymentId: string, paymentId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(validateSinglePaymentStart(paymentId));

    try {
      const response = await validatePayment(providerPaymentId);
      const data = response?.data?.verify_payment;

      const normalizedStatus = normalizePaymentStatus(data.payment_status) as StripeValidationStatus;

      dispatch(
        validateSinglePaymentSuccess({
          paymentId,
          paymentData: {
            validationStatus: normalizedStatus,
            payment_status: data.payment_status,
            amount: data.amount
          }
        })
      );
    } catch (error) {
      dispatch(validateSinglePaymentFail(error));
    }
  };
};

export const initiateSubscriptionPaymentAction = (data: initiateSubscriptionRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(initiateCheckoutStart());

    try {
      const response = await initiateSubscription(data);
      const url = response?.data?.initiate_subscription_plan?.url;

      dispatch(initiateCheckoutSuccess(url));

      if (cb) cb();

      if (url && typeof window !== "undefined") {
        markStripeCheckoutStart();
        window.location.replace(url);
      }
    } catch (error) {
      dispatch(initiateCheckoutFail(error));
    }
  };
};

export const cancelPaymentAction = (entitlementId: string, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(cancelPaymentStart());
    try {
      const response = await searchOrder({
        size: 1,
        from: 0,
        filter: {
          order_filter: {
            entitlement_id: entitlementId
          }
        }
      });

      const orderId = response.data.search_order?.items[0]?.order_id;

      if (!orderId) {
        dispatch(cancelPaymentFail("Order not found"));
      }

      await cancelPayment(orderId);
      dispatch(cancelPaymentSuccess());
      if (cb) cb();
    } catch (err) {
      dispatch(cancelPaymentFail(err));
    }
  };
};

export default stripeTransactionHistorySlice.reducer;
